import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { 
  FiUser, FiPhone, FiMail, FiCalendar, FiDollarSign, 
  FiBriefcase, FiHome, FiCheck, FiX, FiClock, FiEye, FiEyeOff,
  FiDownload, FiPrinter, FiSearch, FiFilter, FiRefreshCw
} from 'react-icons/fi';
import { supabase } from '../../lib/supabase';
import SafeIcon from '../../common/SafeIcon';
import { creditBureauService } from '../../services/creditBureauService';

const CreditApplications = ({ onRunCreditPull }) => {
  const [applications, setApplications] = useState([]);
  const [filteredApplications, setFilteredApplications] = useState([]);
  const [selectedApp, setSelectedApp] = useState(null);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [dateRange, setDateRange] = useState('all');
  const [showDetail, setShowDetail] = useState(false);
  const [creditReport, setCreditReport] = useState(null);
  const [runningCreditCheck, setRunningCreditCheck] = useState(false);
  const [showSSN, setShowSSN] = useState(false); // Toggle for SSN visibility
  const [showDL, setShowDL] = useState(false); // Toggle for Driver's License visibility

  useEffect(() => {
    loadApplications();
  }, []);

  useEffect(() => {
    filterApplications();
  }, [applications, searchTerm, statusFilter, dateRange]);

  const loadApplications = async () => {
    setLoading(true);
    try {
      const { data, error } = await supabase
        .from('finance_applications')
        .select('*')
        .order('created_at', { ascending: false });

      if (error) throw error;
      console.log('Loaded applications:', data); // Debug log to see what's in database
      if (data && data.length > 0) {
        console.log('First application SSN:', data[0].ssn);
        console.log('First application DL:', data[0].drivers_license);
      }
      setApplications(data || []);
    } catch (err) {
      console.error('Error loading credit applications:', err);
    } finally {
      setLoading(false);
    }
  };

  const filterApplications = () => {
    let filtered = [...applications];

    // Search filter
    if (searchTerm) {
      filtered = filtered.filter(app => 
        app.first_name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        app.last_name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        app.email?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        app.phone?.includes(searchTerm)
      );
    }

    // Status filter
    if (statusFilter !== 'all') {
      filtered = filtered.filter(app => app.status === statusFilter);
    }

    // Date range filter
    if (dateRange !== 'all') {
      const now = new Date();
      const cutoff = new Date();
      
      switch(dateRange) {
        case 'today':
          cutoff.setHours(0, 0, 0, 0);
          break;
        case 'week':
          cutoff.setDate(now.getDate() - 7);
          break;
        case 'month':
          cutoff.setMonth(now.getMonth() - 1);
          break;
      }
      
      filtered = filtered.filter(app => 
        new Date(app.created_at) >= cutoff
      );
    }

    setFilteredApplications(filtered);
  };

  const updateStatus = async (appId, newStatus) => {
    try {
      const { error } = await supabase
        .from('finance_applications')
        .update({ 
          status: newStatus,
          updated_at: new Date().toISOString()
        })
        .eq('id', appId);

      if (error) throw error;

      // Update local state
      setApplications(prev => 
        prev.map(app => 
          app.id === appId ? { ...app, status: newStatus } : app
        )
      );

      if (selectedApp?.id === appId) {
        setSelectedApp(prev => ({ ...prev, status: newStatus }));
      }
    } catch (err) {
      console.error('Error updating application status:', err);
    }
  };

  const getStatusColor = (status) => {
    switch(status) {
      case 'pending': return 'text-yellow-500 bg-yellow-50';
      case 'approved': return 'text-green-500 bg-green-50';
      case 'declined': return 'text-red-500 bg-red-50';
      case 'reviewing': return 'text-blue-500 bg-blue-50';
      default: return 'text-gray-500 bg-gray-50';
    }
  };

  const calculateCreditScore = (app) => {
    // Simple scoring algorithm based on available data
    let score = 600; // Base score
    
    // Income factor
    const monthlyIncome = parseFloat(app.monthly_income) || 0;
    const additionalIncome = parseFloat(app.additional_income) || 0;
    const totalIncome = monthlyIncome + additionalIncome;
    
    if (totalIncome > 8000) score += 100;
    else if (totalIncome > 5000) score += 75;
    else if (totalIncome > 3000) score += 50;
    else if (totalIncome > 2000) score += 25;
    
    // Employment stability
    if (app.time_employed?.includes('year')) {
      const years = parseInt(app.time_employed) || 0;
      score += Math.min(years * 10, 50);
    }
    
    // Housing stability
    if (app.rent_or_own === 'own') score += 30;
    if (app.time_at_address?.includes('year')) {
      const years = parseInt(app.time_at_address) || 0;
      score += Math.min(years * 5, 25);
    }
    
    // Down payment factor
    const downPayment = parseFloat(app.down_payment) || 0;
    if (downPayment > 5000) score += 50;
    else if (downPayment > 2000) score += 30;
    else if (downPayment > 1000) score += 15;
    
    return Math.min(score, 850); // Cap at 850
  };

  const calculateDTI = (app) => {
    // Debt-to-Income ratio calculation
    const monthlyIncome = parseFloat(app.monthly_income) || 0;
    const additionalIncome = parseFloat(app.additional_income) || 0;
    const totalIncome = monthlyIncome + additionalIncome;
    
    const monthlyPayment = parseFloat(app.monthly_payment) || 0;
    const estimatedCarPayment = 350; // Estimated car payment
    
    if (totalIncome === 0) return 0;
    return ((monthlyPayment + estimatedCarPayment) / totalIncome * 100).toFixed(1);
  };

  const runCreditCheck = async (checkType = 'soft') => {
    if (!selectedApp) return;
    
    setRunningCreditCheck(true);
    try {
      // Check if we have recent credit report (within 30 days)
      const hasRecent = await creditBureauService.hasRecentInquiry(selectedApp.ssn);
      if (hasRecent && checkType === 'hard') {
        if (!confirm('This applicant has a recent credit inquiry. Run another hard pull?')) {
          setRunningCreditCheck(false);
          return;
        }
      }
      
      // Run the credit check
      const report = await creditBureauService.runCreditCheck(selectedApp, checkType);
      
      // Save report to database
      await supabase
        .from('finance_applications')
        .update({ 
          credit_report: report,
          credit_score: report.scores.average,
          updated_at: new Date().toISOString()
        })
        .eq('id', selectedApp.id);
      
      // Log for compliance
      await creditBureauService.logCreditInquiry(selectedApp.id, report);
      
      setCreditReport(report);
      
      // Update local state
      setSelectedApp(prev => ({ ...prev, credit_report: report }));
      
      alert(`Credit check complete! Score: ${report.scores.average}`);
    } catch (error) {
      console.error('Credit check failed:', error);
      alert('Credit check failed. Please check API configuration.');
    } finally {
      setRunningCreditCheck(false);
    }
  };

  const exportToCSV = () => {
    const headers = ['Date', 'Name', 'Email', 'Phone', 'Income', 'Status'];
    const rows = filteredApplications.map(app => [
      new Date(app.created_at).toLocaleDateString(),
      `${app.first_name} ${app.last_name}`,
      app.email,
      app.phone,
      `$${app.monthly_income}`,
      app.status
    ]);
    
    const csvContent = [headers, ...rows]
      .map(row => row.join(','))
      .join('\n');
    
    const blob = new Blob([csvContent], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `credit-applications-${new Date().toISOString().split('T')[0]}.csv`;
    link.click();
  };

  return (
    <div className="p-6">
      {/* Header */}
      <div className="flex justify-between items-center mb-6">
        <div>
          <h2 className="text-2xl font-bold text-gray-800">Credit Applications</h2>
          <p className="text-gray-600">Manage and review finance applications</p>
        </div>
        <div className="flex gap-2">
          <button
            onClick={loadApplications}
            className="px-4 py-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 flex items-center gap-2"
          >
            <FiRefreshCw size={16} />
            Refresh
          </button>
          <button
            onClick={exportToCSV}
            className="px-4 py-2 bg-blue-500 text-white rounded-lg hover:bg-blue-600 flex items-center gap-2"
          >
            <FiDownload size={16} />
            Export CSV
          </button>
        </div>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6">
        <div className="bg-white rounded-lg shadow-md p-4">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-gray-600 text-sm">Total Applications</p>
              <p className="text-2xl font-bold">{applications.length}</p>
            </div>
            <div className="text-blue-500">
              <FiUser size={24} />
            </div>
          </div>
        </div>
        <div className="bg-white rounded-lg shadow-md p-4">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-gray-600 text-sm">Pending Review</p>
              <p className="text-2xl font-bold text-yellow-500">
                {applications.filter(a => a.status === 'pending').length}
              </p>
            </div>
            <div className="text-yellow-500">
              <FiClock size={24} />
            </div>
          </div>
        </div>
        <div className="bg-white rounded-lg shadow-md p-4">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-gray-600 text-sm">Approved</p>
              <p className="text-2xl font-bold text-green-500">
                {applications.filter(a => a.status === 'approved').length}
              </p>
            </div>
            <div className="text-green-500">
              <FiCheck size={24} />
            </div>
          </div>
        </div>
        <div className="bg-white rounded-lg shadow-md p-4">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-gray-600 text-sm">Declined</p>
              <p className="text-2xl font-bold text-red-500">
                {applications.filter(a => a.status === 'declined').length}
              </p>
            </div>
            <div className="text-red-500">
              <FiX size={24} />
            </div>
          </div>
        </div>
      </div>

      {/* Filters */}
      <div className="bg-white rounded-lg shadow-md p-4 mb-6">
        <div className="flex flex-wrap gap-4 items-center">
          <div className="flex-1 min-w-[200px]">
            <div className="relative">
              <FiSearch className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" />
              <input
                type="text"
                placeholder="Search by name, email, or phone..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full pl-10 pr-4 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
            </div>
          </div>
          <select
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value)}
            className="px-4 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
          >
            <option value="all">All Status</option>
            <option value="pending">Pending</option>
            <option value="reviewing">Reviewing</option>
            <option value="approved">Approved</option>
            <option value="declined">Declined</option>
          </select>
          <select
            value={dateRange}
            onChange={(e) => setDateRange(e.target.value)}
            className="px-4 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
          >
            <option value="all">All Time</option>
            <option value="today">Today</option>
            <option value="week">Past Week</option>
            <option value="month">Past Month</option>
          </select>
        </div>
      </div>

      {/* Applications Table */}
      <div className="bg-white rounded-lg shadow-md overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-gray-50 border-b">
              <tr>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Applicant
                </th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Contact
                </th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Income
                </th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Credit Score
                </th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  DTI Ratio
                </th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Status
                </th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Date
                </th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200">
              {loading ? (
                <tr>
                  <td colSpan="8" className="px-4 py-8 text-center text-gray-500">
                    Loading applications...
                  </td>
                </tr>
              ) : filteredApplications.length === 0 ? (
                <tr>
                  <td colSpan="8" className="px-4 py-8 text-center text-gray-500">
                    No applications found
                  </td>
                </tr>
              ) : (
                filteredApplications.map((app) => (
                  <tr key={app.id} className="hover:bg-gray-50">
                    <td className="px-4 py-3">
                      <div className="flex items-center">
                        <div className="w-10 h-10 bg-blue-100 rounded-full flex items-center justify-center">
                          <FiUser className="text-blue-600" />
                        </div>
                        <div className="ml-3">
                          <p className="font-medium text-gray-900">
                            {app.first_name} {app.last_name}
                          </p>
                          <p className="text-sm text-gray-500">
                            {app.employer_name}
                          </p>
                        </div>
                      </div>
                    </td>
                    <td className="px-4 py-3">
                      <div className="text-sm">
                        <p className="text-gray-900">{app.email}</p>
                        <p className="text-gray-500">{app.phone}</p>
                      </div>
                    </td>
                    <td className="px-4 py-3">
                      <div className="text-sm">
                        <p className="font-medium text-gray-900">
                          ${parseFloat(app.monthly_income || 0).toLocaleString()}
                        </p>
                        {app.additional_income && (
                          <p className="text-gray-500">
                            +${parseFloat(app.additional_income).toLocaleString()}
                          </p>
                        )}
                      </div>
                    </td>
                    <td className="px-4 py-3">
                      <div className="flex items-center">
                        <span className={`font-medium ${
                          calculateCreditScore(app) >= 700 ? 'text-green-600' :
                          calculateCreditScore(app) >= 600 ? 'text-yellow-600' :
                          'text-red-600'
                        }`}>
                          {calculateCreditScore(app)}
                        </span>
                      </div>
                    </td>
                    <td className="px-4 py-3">
                      <span className={`text-sm font-medium ${
                        calculateDTI(app) <= 36 ? 'text-green-600' :
                        calculateDTI(app) <= 43 ? 'text-yellow-600' :
                        'text-red-600'
                      }`}>
                        {calculateDTI(app)}%
                      </span>
                    </td>
                    <td className="px-4 py-3">
                      <span className={`px-2 py-1 text-xs rounded-full ${getStatusColor(app.status)}`}>
                        {app.status}
                      </span>
                    </td>
                    <td className="px-4 py-3 text-sm text-gray-500">
                      {new Date(app.created_at).toLocaleDateString()}
                    </td>
                    <td className="px-4 py-3">
                      <button
                        onClick={() => {
                          console.log('Selected application:', app);
                          console.log('SSN in selected app:', app.ssn);
                          console.log('DL in selected app:', app.drivers_license);
                          setSelectedApp(app);
                          setShowDetail(true);
                        }}
                        className="text-blue-600 hover:text-blue-800"
                      >
                        <FiEye size={18} />
                      </button>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* Detail Modal */}
      <AnimatePresence>
        {showDetail && selectedApp && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4"
            onClick={() => setShowDetail(false)}
          >
            <motion.div
              initial={{ scale: 0.9, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.9, opacity: 0 }}
              className="bg-white rounded-lg max-w-4xl w-full max-h-[90vh] overflow-y-auto"
              onClick={(e) => e.stopPropagation()}
            >
              {/* Modal Header */}
              <div className="sticky top-0 bg-white border-b p-6 flex justify-between items-center">
                <div>
                  <h3 className="text-xl font-bold">Credit Application Details</h3>
                  <p className="text-gray-600">
                    {selectedApp.first_name} {selectedApp.last_name}
                  </p>
                </div>
                <button
                  onClick={() => setShowDetail(false)}
                  className="text-gray-500 hover:text-gray-700"
                >
                  <FiX size={24} />
                </button>
              </div>

              {/* Modal Content */}
              <div className="p-6">
                {/* Credit Decision Box */}
                <div className="bg-gradient-to-r from-blue-50 to-indigo-50 rounded-lg p-6 mb-6">
                  <div className="flex justify-between items-start mb-4">
                    <div>
                      <h4 className="text-lg font-semibold mb-2">Credit Decision</h4>
                      <div className="flex gap-4">
                        <div>
                          <p className="text-sm text-gray-600">
                            {creditReport || selectedApp.credit_report ? 'Actual Score' : 'Estimated Score'}
                          </p>
                          <p className={`text-2xl font-bold ${
                            (creditReport?.scores?.average || selectedApp.credit_report?.scores?.average || calculateCreditScore(selectedApp)) >= 700 ? 'text-green-600' :
                            (creditReport?.scores?.average || selectedApp.credit_report?.scores?.average || calculateCreditScore(selectedApp)) >= 600 ? 'text-yellow-600' :
                            'text-red-600'
                          }`}>
                            {creditReport?.scores?.average || selectedApp.credit_report?.scores?.average || calculateCreditScore(selectedApp)}
                          </p>
                        </div>
                        <div>
                          <p className="text-sm text-gray-600">DTI Ratio</p>
                          <p className={`text-2xl font-bold ${
                            calculateDTI(selectedApp) <= 36 ? 'text-green-600' :
                            calculateDTI(selectedApp) <= 43 ? 'text-yellow-600' :
                            'text-red-600'
                          }`}>
                            {calculateDTI(selectedApp)}%
                          </p>
                        </div>
                        <div>
                          <p className="text-sm text-gray-600">Down Payment</p>
                          <p className="text-2xl font-bold text-blue-600">
                            ${parseFloat(selectedApp.down_payment || 0).toLocaleString()}
                          </p>
                        </div>
                      </div>
                    </div>
                    <div className="flex flex-col gap-2">
                      <div className="flex gap-2">
                        <button
                          onClick={() => updateStatus(selectedApp.id, 'approved')}
                          className="px-4 py-2 bg-green-500 text-white rounded-lg hover:bg-green-600 flex items-center gap-2"
                        >
                          <FiCheck />
                          Approve
                        </button>
                        <button
                          onClick={() => updateStatus(selectedApp.id, 'declined')}
                          className="px-4 py-2 bg-red-500 text-white rounded-lg hover:bg-red-600 flex items-center gap-2"
                        >
                          <FiX />
                          Decline
                        </button>
                        <button
                          onClick={() => updateStatus(selectedApp.id, 'reviewing')}
                          className="px-4 py-2 bg-blue-500 text-white rounded-lg hover:bg-blue-600 flex items-center gap-2"
                        >
                          <FiClock />
                          Under Review
                        </button>
                      </div>
                      <div className="flex gap-2">
                        <button
                          onClick={() => runCreditCheck('soft')}
                          disabled={runningCreditCheck}
                          className="px-4 py-2 bg-purple-500 text-white rounded-lg hover:bg-purple-600 disabled:bg-gray-400 flex items-center gap-2 text-sm"
                        >
                          <FiRefreshCw className={runningCreditCheck ? 'animate-spin' : ''} />
                          Soft Pull
                        </button>
                        <button
                          onClick={() => runCreditCheck('hard')}
                          disabled={runningCreditCheck}
                          className="px-4 py-2 bg-indigo-500 text-white rounded-lg hover:bg-indigo-600 disabled:bg-gray-400 flex items-center gap-2 text-sm"
                        >
                          <FiRefreshCw className={runningCreditCheck ? 'animate-spin' : ''} />
                          Hard Pull
                        </button>
                      </div>
                      {onRunCreditPull && (
                        <button
                          onClick={() => {
                            setShowDetail(false);
                            onRunCreditPull(selectedApp);
                          }}
                          className="w-full px-4 py-2.5 bg-gradient-to-r from-green-600 to-emerald-600 text-white rounded-lg hover:from-green-700 hover:to-emerald-700 flex items-center justify-center gap-2 text-sm font-semibold shadow-lg"
                        >
                          <FiRefreshCw />
                          Open Full Credit Pull Manager →
                        </button>
                      )}
                    </div>
                  </div>
                </div>

                {/* Application Details Grid */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  {/* Personal Information */}
                  <div className="bg-gray-50 rounded-lg p-4">
                    <h4 className="font-semibold mb-3 flex items-center gap-2">
                      <FiUser />
                      Personal Information
                    </h4>
                    <div className="space-y-2 text-sm">
                      <div className="flex justify-between">
                        <span className="text-gray-600">Full Name:</span>
                        <span className="font-medium">{selectedApp.first_name} {selectedApp.last_name}</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-gray-600">Email:</span>
                        <span className="font-medium">{selectedApp.email}</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-gray-600">Phone:</span>
                        <span className="font-medium">{selectedApp.phone}</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-gray-600">Date of Birth:</span>
                        <span className="font-medium">{selectedApp.date_of_birth}</span>
                      </div>
                      <div className="flex justify-between items-center">
                        <span className="text-gray-600">SSN (for 700Credit):</span>
                        <div className="flex items-center gap-2">
                          <span className="font-medium font-mono">
                            {showSSN ? (selectedApp.ssn || 'Not Provided') : '***-**-****'}
                          </span>
                          <button
                            onClick={() => setShowSSN(!showSSN)}
                            className="text-blue-500 hover:text-blue-700"
                            title={showSSN ? "Hide SSN" : "Show SSN"}
                          >
                            {showSSN ? <FiEyeOff size={16} /> : <FiEye size={16} />}
                          </button>
                        </div>
                      </div>
                      <div className="flex justify-between items-center">
                        <span className="text-gray-600">Driver's License:</span>
                        <div className="flex items-center gap-2">
                          <span className="font-medium font-mono">
                            {showDL ? (selectedApp.drivers_license || 'Not Provided') : '**-*******'}
                          </span>
                          <button
                            onClick={() => setShowDL(!showDL)}
                            className="text-blue-500 hover:text-blue-700"
                            title={showDL ? "Hide DL" : "Show DL"}
                          >
                            {showDL ? <FiEyeOff size={16} /> : <FiEye size={16} />}
                          </button>
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Address Information */}
                  <div className="bg-gray-50 rounded-lg p-4">
                    <h4 className="font-semibold mb-3 flex items-center gap-2">
                      <FiHome />
                      Address Information
                    </h4>
                    <div className="space-y-2 text-sm">
                      <div className="flex justify-between">
                        <span className="text-gray-600">Address:</span>
                        <span className="font-medium">{selectedApp.street_address}</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-gray-600">City, State ZIP:</span>
                        <span className="font-medium">{selectedApp.city}, {selectedApp.state} {selectedApp.zip_code}</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-gray-600">Time at Address:</span>
                        <span className="font-medium">{selectedApp.time_at_address}</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-gray-600">Rent/Own:</span>
                        <span className="font-medium">{selectedApp.rent_or_own}</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-gray-600">Monthly Payment:</span>
                        <span className="font-medium">${selectedApp.monthly_payment}</span>
                      </div>
                    </div>
                  </div>

                  {/* Employment Information */}
                  <div className="bg-gray-50 rounded-lg p-4">
                    <h4 className="font-semibold mb-3 flex items-center gap-2">
                      <FiBriefcase />
                      Employment Information
                    </h4>
                    <div className="space-y-2 text-sm">
                      <div className="flex justify-between">
                        <span className="text-gray-600">Employer:</span>
                        <span className="font-medium">{selectedApp.employer_name}</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-gray-600">Job Title:</span>
                        <span className="font-medium">{selectedApp.job_title}</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-gray-600">Time Employed:</span>
                        <span className="font-medium">{selectedApp.time_employed}</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-gray-600">Monthly Income:</span>
                        <span className="font-medium">${selectedApp.monthly_income}</span>
                      </div>
                      {selectedApp.additional_income && (
                        <div className="flex justify-between">
                          <span className="text-gray-600">Additional Income:</span>
                          <span className="font-medium">${selectedApp.additional_income}</span>
                        </div>
                      )}
                    </div>
                  </div>

                  {/* Vehicle Information */}
                  <div className="bg-gray-50 rounded-lg p-4">
                    <h4 className="font-semibold mb-3 flex items-center gap-2">
                      <FiDollarSign />
                      Vehicle & Loan Information
                    </h4>
                    <div className="space-y-2 text-sm">
                      <div className="flex justify-between">
                        <span className="text-gray-600">Vehicle Interest:</span>
                        <span className="font-medium">{selectedApp.vehicle_interest || 'Not specified'}</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-gray-600">Down Payment:</span>
                        <span className="font-medium">${selectedApp.down_payment || 0}</span>
                      </div>
                      {selectedApp.trade_in && (
                        <>
                          <div className="flex justify-between">
                            <span className="text-gray-600">Trade-In:</span>
                            <span className="font-medium">
                              {selectedApp.trade_in_year} {selectedApp.trade_in_make} {selectedApp.trade_in_model}
                            </span>
                          </div>
                          <div className="flex justify-between">
                            <span className="text-gray-600">Trade-In Payoff:</span>
                            <span className="font-medium">${selectedApp.trade_in_payoff || 0}</span>
                          </div>
                        </>
                      )}
                    </div>
                  </div>
                </div>

                {/* Action Buttons */}
                <div className="mt-6 flex gap-4 justify-end">
                  <button
                    onClick={() => window.print()}
                    className="px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 flex items-center gap-2"
                  >
                    <FiPrinter />
                    Print Application
                  </button>
                  <button
                    className="px-4 py-2 bg-blue-500 text-white rounded-lg hover:bg-blue-600 flex items-center gap-2"
                  >
                    <FiMail />
                    Send to Lender
                  </button>
                </div>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

export default CreditApplications;
